import { useAuth } from '../contexts/AuthContext';
import { useMemo, useEffect, useState } from 'react';
import errorLogger from '../services/errorLogger.service';

// Create logger for this component
const logger = errorLogger.createComponentLogger('useAuthWithCustomerOverride');

/**
 * Hook that extends useAuth to support "Login as Customer" functionality.
 * Uses session storage to avoid exposing customer info in URLs.
 */
export const useAuthWithCustomerOverride = () => {
  const auth = useAuth();
  const [customerViewState, setCustomerViewState] = useState<{ customerId: number; customerName: string } | null>(null);
  
  // Simple approach: Just check sessionStorage for loginAsCustomer
  useEffect(() => {
    const checkSessionStorage = () => {
      const sessionData = sessionStorage.getItem('loginAsCustomer');
      logger.debug('Checking sessionStorage:', sessionData);
      if (sessionData) {
        try {
          const data = JSON.parse(sessionData);
          logger.debug('Found customer view data:', data);
          setCustomerViewState(data);
        } catch (error) {
          logger.error('Error parsing loginAsCustomer data:', error);
          setCustomerViewState(null);
        }
      } else {
        setCustomerViewState(null);
      }
    };
    
    // Check initially
    checkSessionStorage();
    
    // Listen for changes
    const handleLoginAsChange = (event: any) => {
      logger.debug('loginAsCustomerChanged event received:', event.detail);
      checkSessionStorage();
    };
    
    window.addEventListener('loginAsCustomerChanged', handleLoginAsChange);
    
    return () => {
      window.removeEventListener('loginAsCustomerChanged', handleLoginAsChange);
    };
  }, [auth.member]);
  
  // Create modified member object if in customer view mode
  const modifiedMember = useMemo(() => {
    logger.debug('Creating member:', {
      hasMember: !!auth.member,
      customerViewState,
      originalCustomerId: auth.member?.customerID,
      willOverride: !!(auth.member && customerViewState)
    });
    
    if (!auth.member || !customerViewState) {
      logger.debug('No override - returning original member');
      return auth.member;
    }
    
    logger.debug(`OVERRIDING customerID from ${auth.member.customerID} to ${customerViewState.customerId}`);
    
    // Override the customerID to simulate being logged in as that customer's admin
    return {
      ...auth.member,
      customerID: customerViewState.customerId,
      customer_id: customerViewState.customerId,
      // Keep the original member type (master_admin/reseller_admin) for permissions
      // but add a flag to indicate this is a "login as" session
      isLoginAs: true,
      originalCustomerId: auth.member.customerID,
      loginAsCustomerName: customerViewState.customerName,
    };
  }, [auth.member, customerViewState]);
  
  const result = {
    ...auth,
    member: modifiedMember,
    isLoginAsCustomer: !!customerViewState,
  };
  
  // Debug log the final result
  if (customerViewState) {
    logger.debug('Returning:', {
      isLoginAsCustomer: result.isLoginAsCustomer,
      modifiedCustomerID: modifiedMember?.customerID,
      originalCustomerID: auth.member?.customerID
    });
  }
  
  return result;
};